'use client';
import React, { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

import { YM_COUNTER_ID } from '@/shared/const/ym';

declare global {
  // eslint-disable-next-line unused-imports/no-unused-vars -- declare global
  interface Window {
    ym?: (..._args: unknown[]) => void;
  }
}

/**
 * Yandex Metrica грузится по первому взаимодействию пользователя (тап/скролл/
 * клавиша) или по фолбэк-таймеру. lazyOnload было недостаточно: Lighthouse
 * дожидается load-события и всё равно засчитывал tag.js (87 КиБ, ~113 мс
 * main-thread) и ошибку его WebSocket (wss://mc.yandex.com/solid.ws →
 * ERR_NAME_NOT_RESOLVED — сокет открывает сам tag.js, настройками init он
 * не отключается).
 *
 * Стаб window.ym создаётся при монтировании, поэтому вызовы ym(...) до
 * загрузки tag.js (в т.ч. sendYMEvent) складываются в очередь и не теряются.
 * Цена подхода: визиты короче фолбэк-таймера без единого взаимодействия
 * не попадут в счётчик (доля таких визитов мала, обычный компромисс
 * «ленивой аналитики»).
 */
const FALLBACK_DELAY_MS = 10_000;

const INTERACTION_EVENTS = [
  'pointerdown',
  'keydown',
  'scroll',
  'touchstart',
] as const;

export const YMetrika = () => {
  const pathname = usePathname();
  const isFirstHit = useRef(true);

  useEffect(() => {
    if (!YM_COUNTER_ID) {
      return;
    }
    // Первый просмотр отправляет init-сниппет ниже (после загрузки tag.js),
    // здесь шлём хиты только на SPA-переходы.
    if (isFirstHit.current) {
      isFirstHit.current = false;
      return;
    }
    window.ym?.(YM_COUNTER_ID, 'hit', window.location.href);
  }, [pathname]);

  useEffect(() => {
    if (!YM_COUNTER_ID) {
      return;
    }

    // Стаб-очередь 1-в-1 как в официальном сниппете Метрики: tag.js после
    // загрузки читает ym.a (массив arguments-объектов) и проигрывает вызовы
    if (!window.ym) {
      const stub = function () {
        // eslint-disable-next-line prefer-rest-params
        (stub.a = stub.a || []).push(arguments);
      } as ((..._args: unknown[]) => void) & { a?: IArguments[]; l?: number };
      stub.l = Date.now();
      window.ym = stub;
    }

    let loaded = false;
    let timerId: ReturnType<typeof setTimeout> | undefined;

    const load = () => {
      if (loaded) {
        return;
      }
      loaded = true;
      cleanup();

      const src = 'https://mc.yandex.ru/metrika/tag.js';
      for (let i = 0; i < document.scripts.length; i++) {
        if (document.scripts[i].src === src) {
          return;
        }
      }
      const script = document.createElement('script');
      script.async = true;
      script.src = src;
      document.head.appendChild(script);

      window.ym?.(YM_COUNTER_ID, 'init', {
        defer: true,
        clickmap: true,
        trackLinks: true,
        accurateTrackBounce: true,
        // webvisor и так выключен; solid.ws-сокет tag.js открывает сам —
        // при отложенной загрузке его ошибка не попадает в lab-трейс
        webvisor: false,
      });
      window.ym?.(YM_COUNTER_ID, 'hit', window.location.href);
    };

    const cleanup = () => {
      INTERACTION_EVENTS.forEach((event) =>
        window.removeEventListener(event, load),
      );
      if (timerId !== undefined) {
        clearTimeout(timerId);
      }
    };

    INTERACTION_EVENTS.forEach((event) =>
      window.addEventListener(event, load, { passive: true }),
    );
    timerId = setTimeout(load, FALLBACK_DELAY_MS);

    return cleanup;
  }, []);

  if (!YM_COUNTER_ID) {
    return null;
  }

  return (
    // Пиксель для посетителей без JS. Через innerHTML, а не JSX: React Float
    // поднимал <img> из <noscript> в <link rel="preload" as="image"> и пиксель
    // конкурировал с hero-картинкой за сеть на старте (аудит LCP load delay).
    <noscript
      dangerouslySetInnerHTML={{
        __html: `<div><img src="https://mc.yandex.ru/watch/${YM_COUNTER_ID}" style="position:absolute;left:-9999px" alt="" /></div>`,
      }}
    />
  );
};
